import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Award, BookOpen, Users, GraduationCap, Building2, Shield, Heart,
  FileText, Briefcase, Globe, TrendingUp, CheckCircle, AlertCircle, Clock,
  RefreshCw, ChevronRight, BarChart3, Target, Download, FileSpreadsheet, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NAACDashboard() {
  const [overview, setOverview] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCriterion, setSelectedCriterion] = useState(null);
  const [criterionDetails, setCriterionDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, readinessRes] = await Promise.all([
        axios.get(`${API_URL}/api/naac-dashboard/overview`, { headers }),
        axios.get(`${API_URL}/api/naac-dashboard/accreditation-readiness`, { headers })
      ]);
      setOverview(overviewRes.data);
      setReadiness(readinessRes.data);
    } catch (err) {
      toast.error('Failed to fetch NAAC data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (criterionKey, criterionData) => {
    setSelectedCriterion({ key: criterionKey, data: criterionData });
    setDetailsLoading(true);
    try {
      const criterionNum = criterionKey.replace('criterion_', '');
      const res = await axios.get(`${API_URL}/api/naac-dashboard/criterion/${criterionNum}`, { headers });
      setCriterionDetails(res.data);
    } catch (err) {
      toast.error('Failed to fetch criterion details');
      setCriterionDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleExport = async (type, format) => {
    try {
      toast.loading(`Generating ${format.toUpperCase()} report...`);
      const response = await axios.get(
        `${API_URL}/api/naac-export/${type}/${format}`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.dismiss();
      toast.success('Report downloaded successfully!');
    } catch (err) {
      toast.dismiss();
      toast.error('Failed to generate report');
    }
  };

  const criterionIcons = {
    criterion_1: BookOpen,
    criterion_2: GraduationCap,
    criterion_3: TrendingUp,
    criterion_4: Building2,
    criterion_5: Users,
    criterion_6: Shield,
    criterion_7: Heart
  };

  const criterionColors = {
    criterion_1: 'blue',
    criterion_2: 'purple',
    criterion_3: 'green',
    criterion_4: 'orange',
    criterion_5: 'cyan',
    criterion_6: 'indigo',
    criterion_7: 'pink'
  };

  const statusColors = {
    green: 'bg-green-100 text-green-800 border-green-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    red: 'bg-red-100 text-red-800 border-red-300'
  };

  const checklistStatusColors = {
    complete: 'bg-green-100 text-green-800',
    partial: 'bg-yellow-100 text-yellow-800',
    pending: 'bg-red-100 text-red-800'
  };

  const checklistStatusIcons = {
    complete: CheckCircle,
    partial: Clock,
    pending: AlertCircle
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="naac-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">NAAC Dashboard</h1>
          <p className="text-gray-500">Comprehensive accreditation overview</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" onClick={() => handleExport('comprehensive', 'excel')}>
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
          </Button>
          <Button onClick={() => handleExport('faculty-research', 'pdf')}>
            <Download className="h-4 w-4 mr-1" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Overall Status Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-blue-100 text-sm">Overall Status</p>
                  <p className="text-2xl font-bold mt-1">{overview.overall_status}</p>
                </div>
                <Award className="h-8 w-8 text-blue-200" />
              </div>
              <div className="mt-3 flex gap-2">
                <span className="bg-green-400/30 px-2 py-1 rounded text-xs">{overview.score_summary.green} Strong</span>
                <span className="bg-yellow-400/30 px-2 py-1 rounded text-xs">{overview.score_summary.yellow} Moderate</span>
                <span className="bg-red-400/30 px-2 py-1 rounded text-xs">{overview.score_summary.red} Needs Work</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm">Readiness Score</p>
                  <p className="text-2xl font-bold mt-1">{readiness?.readiness_score}%</p>
                </div>
                <Target className="h-8 w-8 text-green-500" />
              </div>
              <Progress value={readiness?.readiness_score || 0} className="mt-3" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm">Publications</p>
                  <p className="text-2xl font-bold mt-1">{overview.key_highlights.publications}</p>
                </div>
                <BookOpen className="h-8 w-8 text-purple-500" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Research Output</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm">Placements</p>
                  <p className="text-2xl font-bold mt-1">{overview.key_highlights.placements}</p>
                </div>
                <Briefcase className="h-8 w-8 text-cyan-500" />
              </div>
              <p className="text-xs text-gray-400 mt-2">Students Placed</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Criteria Overview</TabsTrigger>
          <TabsTrigger value="readiness">Readiness Checklist</TabsTrigger>
          <TabsTrigger value="highlights">Key Highlights</TabsTrigger>
        </TabsList>

        {/* Criteria Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {overview && Object.entries(overview.criteria).map(([key, criterion]) => {
              const Icon = criterionIcons[key] || Award;
              const color = criterionColors[key] || 'gray';
              return (
                <Card key={key} className={`border-2 ${statusColors[criterion.score_indicator]}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-full bg-${color}-100 flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 text-${color}-600`} />
                      </div>
                      <div>
                        <CardTitle className="text-sm font-medium">{key.replace('_', ' ').toUpperCase()}</CardTitle>
                        <p className="text-xs text-gray-500 line-clamp-1">{criterion.title}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="space-y-1 text-xs">
                      {Object.entries(criterion.metrics).slice(0, 4).map(([metric, value]) => (
                        <div key={metric} className="flex justify-between">
                          <span className="text-gray-500 capitalize">{metric.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between items-center">
                      <Badge className={statusColors[criterion.score_indicator]}>
                        {criterion.score_indicator === 'green' ? 'Strong' : criterion.score_indicator === 'yellow' ? 'Moderate' : 'Needs Work'}
                      </Badge>
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => handleViewDetails(key, criterion)}>
                        Details <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Readiness Checklist Tab */}
        <TabsContent value="readiness" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Accreditation Readiness Checklist</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">{readiness?.status}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-600">{readiness?.readiness_score}%</p>
                  <p className="text-xs text-gray-400">Readiness Score</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {readiness?.checklist.map((item, index) => {
                  const StatusIcon = checklistStatusIcons[item.status];
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <StatusIcon className={`h-5 w-5 ${item.status === 'complete' ? 'text-green-600' : item.status === 'partial' ? 'text-yellow-600' : 'text-red-600'}`} />
                        <div>
                          <p className="font-medium text-sm">{item.item}</p>
                          <p className="text-xs text-gray-500">Criterion {item.criterion} • Required: {item.required}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={checklistStatusColors[item.status]}>
                          {item.status === 'complete' ? 'Complete' : item.status === 'partial' ? 'In Progress' : 'Pending'}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          Current: {Array.isArray(item.current) ? item.current.length : item.current}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{readiness?.summary.complete}</p>
                  <p className="text-xs text-gray-500">Complete</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{readiness?.summary.partial}</p>
                  <p className="text-xs text-gray-500">In Progress</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{readiness?.summary.pending}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Key Highlights Tab */}
        <TabsContent value="highlights" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Key Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" /> Key Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {overview && Object.entries(overview.key_highlights).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-xl font-bold">
                        {key.includes('percentage') ? `${value}%` : 
                         key.includes('lakhs') ? `₹${value}L` : 
                         value.toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Strengths & Improvements */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" /> Areas of Strength
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {overview?.areas_of_strength.length > 0 ? (
                      overview.areas_of_strength.map((area, i) => (
                        <Badge key={i} className="bg-green-100 text-green-800">{area}</Badge>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">Continue building strengths across all criteria</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" /> Areas for Improvement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {overview?.areas_for_improvement.length > 0 ? (
                      overview.areas_for_improvement.map((area, i) => (
                        <Badge key={i} className="bg-red-100 text-red-800">{area}</Badge>
                      ))
                    ) : (
                      <p className="text-green-600 text-sm">Great! No critical areas identified</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Institution Summary */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" /> Institution Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{overview?.institution.total_students}</p>
                      <p className="text-xs text-gray-500">Students</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-600">{overview?.institution.total_faculty}</p>
                      <p className="text-xs text-gray-500">Faculty</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{overview?.institution.programs}</p>
                      <p className="text-xs text-gray-500">Programs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Criterion Details Modal */}
      <Dialog open={!!selectedCriterion} onOpenChange={() => setSelectedCriterion(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedCriterion && (() => {
                const Icon = criterionIcons[selectedCriterion.key] || Award;
                return <Icon className="h-5 w-5" />;
              })()}
              {selectedCriterion?.data?.title || 'Criterion Details'}
            </DialogTitle>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : criterionDetails ? (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-4">
                <Badge className={statusColors[selectedCriterion?.data?.score_indicator]}>
                  {selectedCriterion?.data?.score_indicator === 'green' ? 'Strong' : 
                   selectedCriterion?.data?.score_indicator === 'yellow' ? 'Moderate' : 'Needs Work'}
                </Badge>
                <span className="text-sm text-gray-500">
                  Score: {selectedCriterion?.data?.score || 0}/100
                </span>
              </div>

              {/* Dynamic Data Display */}
              {Object.entries(criterionDetails).filter(([key]) => !['criterion', 'error'].includes(key)).map(([key, value]) => (
                <div key={key}>
                  <h4 className="font-semibold mb-2 capitalize">{key.replace(/_/g, ' ')}</h4>
                  {Array.isArray(value) ? (
                    value.length > 0 ? (
                      <div className="space-y-2">
                        {value.map((item, i) => (
                          <div key={i} className="bg-gray-50 p-3 rounded-lg">
                            {typeof item === 'object' ? (
                              Object.entries(item).map(([k, v]) => (
                                <p key={k} className="text-sm">
                                  <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}: </span>
                                  <span className="font-medium">{v?.toString() || '-'}</span>
                                </p>
                              ))
                            ) : (
                              <p className="text-sm">{item}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No data available</p>
                    )
                  ) : typeof value === 'object' && value !== null ? (
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(value).map(([k, v]) => (
                        <div key={k} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-500 capitalize">{k.replace(/_/g, ' ')}</p>
                          <p className="font-semibold text-lg">{typeof v === 'number' ? v.toLocaleString('en-IN') : v?.toString() || '-'}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="bg-gray-50 p-3 rounded-lg font-medium">
                      {typeof value === 'number' ? value.toLocaleString('en-IN') : value?.toString() || '-'}
                    </p>
                  )}
                </div>
              ))}

              {/* Show message if no data */}
              {Object.keys(criterionDetails).filter(k => !['criterion', 'error'].includes(k)).length === 0 && (
                <p className="text-center text-gray-500 py-4">No detailed data available for this criterion</p>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No details available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
